import Image from 'next/image'
import {FC} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {useTramaInMediaContent} from '@/content/media'
import {useTranslation} from '@/i18n'
import {HOVER_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

export const TramaInMedia: FC<React.PropsWithChildren<unknown>> = () => {
  const {t} = useTranslation()
  const {title, articles} = useTramaInMediaContent()

  return (
    <>
      <Text variant={TextVariant.h2} align="center">
        {t(title)}
      </Text>
      <ul className="list">
        {articles.map(({logo, altLogo, linkHref, linkText, openInNewTab}) => (
          <li key={linkHref} className="item">
            <a
              href={linkHref}
              {...(openInNewTab
                ? {target: '_blank', rel: 'noopener noreferrer'}
                : {target: '_self'})}
              className="link"
            >
              <div className="logo-wrapper">
                <div className="logo">
                  <Image
                    src={logo}
                    alt={altLogo}
                    style={{objectFit: 'contain'}}
                    fill
                    sizes={`${MOBILE_SCREENSIZE} 160px, 200px`}
                  />
                </div>
              </div>
              <div className="wrapper">
                <Text element="h3" fontWeight={TextFontWeight.bold}>
                  {t(linkText)}
                </Text>
              </div>
            </a>
          </li>
        ))}
      </ul>

      <style jsx>{`
        .list {
          display: flex;
          justify-content: space-between;
          margin-top: ${theme.spacing[6]};
          margin-left: -${theme.spacing[3]};
          margin-right: -${theme.spacing[3]};
        }
        .item {
          flex: 0 0 255px;
          margin-top: ${theme.spacing[5]};
          margin-left: ${theme.spacing[3]};
          margin-right: ${theme.spacing[3]};
          border-radius: 12px;
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[200]};
          transition: box-shadow ${HOVER_TRANSITION}ms ease-in-out;
        }
        .item:hover,
        .item:hover .logo-wrapper {
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[300]};
        }
        .link {
          display: block;
          height: 100%;
        }
        .logo-wrapper {
          padding: ${theme.spacing[4]} ${theme.spacing[3]};
          background-color: ${theme.palette.background.butter};
          border-top-left-radius: 12px;
          border-top-right-radius: 12px;
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[200]};
        }
        .logo {
          position: relative;
          width: 200px;
          height: 40px;
          margin: 0 auto;
        }
        .wrapper {
          padding: ${theme.spacing[3]};
        }
        @media ${TABLET_SCREENSIZE} {
          .list {
            flex-wrap: wrap;
            justify-content: center;
            margin-top: ${theme.spacing[5]};
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .list {
            margin-top: 0;
            margin-left: 0;
            margin-right: 0;
          }
          .item {
            flex: 1 0 100%;
            margin-left: 0;
            margin-right: 0;
            margin-top: ${theme.spacing[6]};
          }
          .item + .item {
            margin-top: ${theme.spacing[4]};
          }
          .logo {
            width: 160px;
            height: 36px;
          }
        }
      `}</style>
    </>
  )
}
